import { ChangeEvent, useState } from 'react';

import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Typography,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { useSearchQuery } from 'contexts/SearchQueryContext';
import { ContatoMencionado } from 'types';
import { getMentionColors } from 'utils/mentionColors';
import { renderTextWithMentions } from 'utils/renderTextWithMentions';

type LegendaMensagemProps = {
  legenda?: string;
  isMe?: boolean;
  contatosMencionados?: ContatoMencionado[];
};

const maxWidth = 40; // quantidade máxima de caracteres visiveis na legenda sem a necessidade de expandir

const Accordion = withStyles({
  root: {
    boxShadow: 'none',
    background: 'none !important',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    padding: 0,
    marginBottom: -8,
    minHeight: 0,
    '&$expanded': {
      minHeight: 0,
    },
  },
  content: {
    overflow: 'hidden',
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles(({ spacing }) => ({
  root: {
    padding: spacing(0),
  },
}))(MuiAccordionDetails);

const useStyles = makeStyles(({ typography, palette }) => ({
  heading: ({ isMe }: LegendaMensagemProps) => ({
    color: isMe ? palette.common.white : palette.text.primary,
    fontSize: typography.pxToRem(14),
    fontWeight: typography.fontWeightRegular as number,
    maxWidth: `${maxWidth}ch`,
    '& a.mention': {
      borderRadius: 4,
      ...getMentionColors(palette),
      paddingInline: 4,
      fontWeight: 500,
      textDecoration: 'none',
      cursor: 'pointer',
      pointerEvents: 'auto',
    },
  }),
  contentText: ({ isMe }: LegendaMensagemProps) => ({
    color: isMe ? palette.common.white : palette.text.primary,
    wordBreak: 'break-word',
    '& a.mention': {
      borderRadius: 4,
      ...getMentionColors(palette),
      paddingInline: 4,
      fontWeight: 500,
      textDecoration: 'none',
      cursor: 'pointer',
      pointerEvents: 'auto',
    },
  }),
  expandedIcon: ({ isMe }: LegendaMensagemProps) => ({
    color: isMe ? palette.common.white : palette.text.primary,
  }),
}));

export default function LegendaMensagem({
  legenda,
  isMe,
  contatosMencionados,
}: LegendaMensagemProps) {
  const classes = useStyles({ isMe });
  const searchQuery = useSearchQuery();
  const [expanded, setExpanded] = useState<string | false>(false);
  // Só habilita a expansão se o conteúdo da legenda for grande
  const disabled = legenda && legenda?.length <= maxWidth;

  const handleChange =
    (panel: string) =>
    (event: ChangeEvent<Record<string, never>>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  if (legenda) {
    return (
      <Accordion
        expanded={expanded === 'panel1'}
        onChange={disabled ? undefined : handleChange('panel1')}
      >
        <AccordionSummary
          style={{ cursor: disabled ? 'default' : 'inherit' }}
          expandIcon={
            disabled ? null : (
              <ExpandMoreIcon className={classes.expandedIcon} />
            )
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography
            className={classes.heading}
            component="div"
            noWrap
            dangerouslySetInnerHTML={{
              __html: expanded
                ? ''
                : renderTextWithMentions(
                    legenda,
                    contatosMencionados ?? [],
                    searchQuery,
                  ).replace(/<br \/>/g, ' '),
            }}
          />
        </AccordionSummary>

        <AccordionDetails>
          <Typography
            variant="body2"
            className={classes.contentText}
            dangerouslySetInnerHTML={{
              __html: renderTextWithMentions(
                legenda,
                contatosMencionados ?? [],
                searchQuery,
              ),
            }}
          />
        </AccordionDetails>
      </Accordion>
    );
  }

  return null;
}
